import { useEffect, useMemo, useState } from 'react';
import type { SiteAssetDto, SiteAssetSlug } from '@igs/shared';
import { listPublicSiteAssets } from './site-assets-api.js';

export interface SiteAssetsState {
  readonly loading: boolean;
  readonly getAsset: (slug: SiteAssetSlug) => SiteAssetDto | null;
}

// Lightweight read-once hook for public consumers (HomePage, Header).
// We treat empty publicId as "not set" — surfaces as `null` to the caller so
// it can render its fallback text/layout instead of a broken <img>.
export function useSiteAssets(): SiteAssetsState {
  const [items, setItems] = useState<readonly SiteAssetDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void listPublicSiteAssets()
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const bySlug = useMemo(() => {
    const map = new Map<SiteAssetSlug, SiteAssetDto>();
    for (const item of items) map.set(item.slug, item);
    return map;
  }, [items]);

  const getAsset = useMemo(
    () =>
      (slug: SiteAssetSlug): SiteAssetDto | null => {
        const item = bySlug.get(slug);
        if (!item || !item.publicId) return null;
        return item;
      },
    [bySlug],
  );

  return { loading, getAsset };
}
